import React, { useCallback, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// MUI
import Typography from '@mui/material/Typography';

// Components
import StepComponent from '../Step';
import FinishedSection from './FinishedSection';
import SlotTracking from '../../../components/SlotTracking';

// Context
import { useOnboardingContext } from '../context';

// Types
import { Section, Step } from '../types';

// Utils
import { getSectionSteps, SECTION_ORDER } from '../utils';

// Styles
import styles from './style.module.scss';


type SectionProps = {
  section: Section; // The section to render
  onNextSection: () => void;
  isLastSection: boolean;
}

const SectionComponent: React.FC<SectionProps> = ({ section, onNextSection, isLastSection }) => {
  // i18n
  const { t } = useTranslation();

  // Context
  const { setShowBackground } = useOnboardingContext();

  // Title
  const SectionTitle = useMemo(() => t(`onboarding.${section}.title`), [section, t]);
  const SectionIndex = useMemo(() => SECTION_ORDER.indexOf(section), [section]);
  const SectionFinishedText = useMemo(() => t(`onboarding.${section}.finished`), [section, t]);
  const steps = useMemo(() => {
    return getSectionSteps(section, t);
  }, [section, t]);

  // State
  const [currentStep, setCurrentStep] = useState<Step>(steps[0]);
  const [isLastStep, setIsLastStep] = useState(false);

  // Effects
  useEffect(() => {
    if (section) {
      setCurrentStep(steps[0]);
      setIsLastStep(false);
    }
  }, [section, steps]);

  // Handlers
  const handleNextStep = useCallback(() => {
    const nextStepIndex = steps.indexOf(currentStep) + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex]);
    } else {
      // If we're at the last step, mark section as finished
      setIsLastStep(true);
      setShowBackground(true);
    }
  }, [currentStep, steps, setShowBackground]);

  const handleSkipStep = useCallback(() => {
    const nextStepIndex = steps.indexOf(currentStep) + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex]);
    } else {
      // If we're at the last step, mark section as finished
      setIsLastStep(true);
      setShowBackground(true);
    }
  }, [currentStep, steps, setShowBackground]);

  const handleNextSection = useCallback(() => {
    onNextSection();
    setShowBackground(false);
    setIsLastStep(false);
  }, [onNextSection, setShowBackground]);

  // Render
  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionContent}>
        <Typography variant="body1" component="p" color="text.secondary">
          {t('onboarding.common.section_index', { index: SectionIndex + 1 })}
        </Typography>

        <Typography variant="h4" component="p" sx={{ marginTop: 2 }}>
          {SectionTitle}
        </Typography>

        <SlotTracking
          currentSlot={SectionIndex}
          totalSlots={SECTION_ORDER.length}
        />

        {currentStep && !isLastStep && (
          <StepComponent
            step={currentStep}
            section={section}
            stepIndex={steps.indexOf(currentStep) + 1}
            totalSteps={steps.length}
            onNext={handleNextStep}
            onSkip={handleSkipStep}
          />
        )}

        {isLastStep && (
          <FinishedSection
            text={SectionFinishedText}
            isLastSection={isLastSection}
            onNextSection={handleNextSection}
          />
        )}
      </div>
    </div>
  )
}

export default SectionComponent;
